import { matchPath } from 'react-router-dom';
import { isCategorySlug, categoryBySlug } from './categorySlugs';
import { isPeriodSlug } from './periodSlugs';

/**
 * Path constants for the public territory routes ("Homepage precompilata
 * per territorio", T014-FE of docs/IMPLEMENTATION_PLAN.md) and the category
 * x territory routes (T011-FE of docs/IMPLEMENTATION_PLAN_SEO_CATEGORY_PAGES.md).
 *
 * `REGION_OR_PROVINCE` and `COMUNE` render the existing `HomePage` — no new
 * component, per R11 — with the location preset from the resolved territory
 * instead of the user's saved preference.
 *
 * `CATEGORY_COMUNE` (`/eventi/:a/:b/:c`) is shared by two different address
 * shapes that the server (`parseTerritoryPath`, T004-BE) disambiguates by
 * position, not by pattern: three real segments (`comune-category`), OR two
 * segments where the third slot is actually empty and `:b` is a category
 * slug (`single-category`, e.g. `/eventi/lombardia/sagre`) — that second
 * shape matches `COMUNE`, not `CATEGORY_COMUNE`. `parseCategoryRoute` below
 * reproduces the server's exact precedence so the client never disagrees
 * about which route is a category page (R9).
 */
export const TERRITORY_PATHS = {
  REGION_OR_PROVINCE: '/eventi/:a',
  COMUNE: '/eventi/:a/:b',
  CATEGORY_COMUNE: '/eventi/:a/:b/:c',
} as const;

/**
 * True when `pathname` is a territory route (`/eventi/:a`, `/eventi/:a/:b`,
 * or `/eventi/:a/:b/:c`) — including category routes.
 *
 * Used to know, SYNCHRONOUSLY and before any data fetch, that the homepage
 * (or Esplora, via T011-FE) is about to receive a location override from
 * `/api/territory` — see `UserContext`'s `locationResolutionPending`, which
 * gates `EventsContext` and the homepage gallery hooks so they don't fire a
 * nationwide query that gets thrown away the instant the override lands
 * (T014-FE double-fetch fix, extended to the 3-segment form by T011-FE).
 */
export function isTerritoryPath(pathname: string): boolean {
  return (
    matchPath(TERRITORY_PATHS.CATEGORY_COMUNE, pathname) !== null ||
    matchPath(TERRITORY_PATHS.COMUNE, pathname) !== null ||
    matchPath(TERRITORY_PATHS.REGION_OR_PROVINCE, pathname) !== null
  );
}

export interface CategoryRoute {
  categoryId: string;
  slug: string;
}

/**
 * Resolves a pathname to a category route, mirroring the server's
 * `parseTerritoryPath` precedence exactly (R9, T004-BE):
 *
 * - 3 segments (`/eventi/:a/:b/:c`): a category route only when the THIRD
 *   segment is a category slug AND the SECOND segment is NOT a category
 *   slug (a category can never appear in the middle — mirrors
 *   `api/lib/territory.ts`'s `if (isCategorySlug(b) || !isCategorySlug(c))
 *   return null;`). Any other 3-segment shape — including the malformed
 *   "category in the middle" one (e.g. `/eventi/roma/sagre/mercatini`,
 *   where both `b` and `c` are valid category slugs) — is not a category
 *   route; the server 404s it and this function returns `null` so the
 *   client doesn't disagree.
 * - 2 segments (`/eventi/:a/:b`): a category route only when the SECOND
 *   segment is a category slug (region/province + category, e.g.
 *   `/eventi/lombardia/sagre`). Otherwise it's a comune route.
 * - 1 segment: never a category route.
 *
 * Returns `null` for anything that isn't a category route, including plain
 * territory routes — callers (`App.tsx`'s `TerritoryRouteElement`,
 * `useEsploraFiltersBootstrap`) treat `null` as "not a category page".
 */
export function parseCategoryRoute(pathname: string): CategoryRoute | null {
  const threeMatch = matchPath(TERRITORY_PATHS.CATEGORY_COMUNE, pathname);
  if (threeMatch) {
    const { b, c } = threeMatch.params;
    if (!c || isCategorySlug(b ?? '') || isPeriodSlug(b ?? '') || !isCategorySlug(c)) return null;
    const category = categoryBySlug(c);
    return category ? { categoryId: category.id, slug: c } : null;
  }

  const twoMatch = matchPath(TERRITORY_PATHS.COMUNE, pathname);
  if (twoMatch) {
    const slug = twoMatch.params.b;
    if (!slug || !isCategorySlug(slug)) return null;
    const category = categoryBySlug(slug);
    return category ? { categoryId: category.id, slug } : null;
  }

  return null;
}

export interface PresetRoute {
  segments: string[];
  category: CategoryRoute | null;
  periodSlug: string | null;
  periodInPath: boolean;
  basePath: string;
}

/** Riconosce la grammatica, senza anticipare il rollout deciso dal server. */
export function parsePresetRoute(pathname: string, search = ''): PresetRoute | null {
  if (!isTerritoryPath(pathname)) return null;
  const segments = pathname.replace(/\/+$/, '').split('/').slice(2);
  const [a, b, c] = segments;
  if (!a || (c && (isCategorySlug(b ?? '') || isPeriodSlug(b ?? '')))) return null;
  if (c && !isCategorySlug(c) && !isPeriodSlug(c)) return null;

  const category = parseCategoryRoute(pathname);
  const suffix = c ?? b;
  const periodInPath = !category && Boolean(suffix && isPeriodSlug(suffix));
  const values = new URLSearchParams(search).getAll('quando');
  const queryPeriod =
    values.length === 1 && isPeriodSlug(values[0] ?? '') ? (values[0] ?? null) : null;
  const periodSlug = periodInPath ? (suffix ?? null) : queryPeriod;
  const baseSegments = category || periodInPath ? segments.slice(0, -1) : segments;
  return {
    segments,
    category,
    periodSlug,
    periodInPath,
    basePath: `/eventi/${baseSegments.join('/')}`,
  };
}

/** Categorie query e parametri di campagna non riaprono il pending né rimontano la pagina. */
export function territoryRouteKey(pathname: string, search = ''): string {
  return `${pathname}|${parsePresetRoute(pathname, search)?.periodSlug ?? ''}`;
}

/** Mantiene il nuovo head SSR quando cambia periodo o percorso di un preset periodo. */
export function requiresTerritoryDocumentNavigation(
  pathname: string,
  search: string,
  target: string
): boolean {
  const [targetPath = '', targetSearch = ''] = target.split('?');
  const current = parsePresetRoute(pathname, search);
  const next = parsePresetRoute(targetPath, targetSearch);
  return (
    current?.periodSlug !== next?.periodSlug ||
    Boolean((current?.periodSlug || next?.periodSlug) && pathname !== targetPath)
  );
}
